import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FastAverageColor } from "fast-average-color";
import characters from "../characters.json";

const DEFAULT_CHARACTER_INDEX = 49;
const DEFAULT_DOMINANT_COLOR = "#de9db4";
const DEFAULT_BACKGROUND_COLOR = "#17141d";
const PLACEHOLDER_BACKGROUND = "#15121a";
const ClipboardItemCtor =
  typeof window !== "undefined" ? window.ClipboardItem : undefined;

function desaturateColor(hex) {
  const red = parseInt(hex.slice(1, 3), 16);
  const green = parseInt(hex.slice(3, 5), 16);
  const blue = parseInt(hex.slice(5, 7), 16);

  const normalizedRed = red / 255;
  const normalizedGreen = green / 255;
  const normalizedBlue = blue / 255;
  const max = Math.max(normalizedRed, normalizedGreen, normalizedBlue);
  const min = Math.min(normalizedRed, normalizedGreen, normalizedBlue);

  let hue = 0;
  let saturation = 0;
  let lightness = (max + min) / 2;

  if (max !== min) {
    const delta = max - min;

    saturation =
      lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);

    if (max === normalizedRed) {
      hue =
        (normalizedGreen - normalizedBlue) / delta +
        (normalizedGreen < normalizedBlue ? 6 : 0);
    } else if (max === normalizedGreen) {
      hue = (normalizedBlue - normalizedRed) / delta + 2;
    } else {
      hue = (normalizedRed - normalizedGreen) / delta + 4;
    }

    hue /= 6;
  }

  saturation *= 0.18;
  lightness = Math.max(0.12, lightness * 0.34);

  const hueToRgb = (p, q, t) => {
    let adjusted = t;

    if (adjusted < 0) adjusted += 1;
    if (adjusted > 1) adjusted -= 1;
    if (adjusted < 1 / 6) return p + (q - p) * 6 * adjusted;
    if (adjusted < 1 / 2) return q;
    if (adjusted < 2 / 3) {
      return p + (q - p) * (2 / 3 - adjusted) * 6;
    }

    return p;
  };

  const q =
    lightness < 0.5
      ? lightness * (1 + saturation)
      : lightness + saturation - lightness * saturation;
  const p = 2 * lightness - q;

  const nextRed = Math.round(hueToRgb(p, q, hue + 1 / 3) * 255);
  const nextGreen = Math.round(hueToRgb(p, q, hue) * 255);
  const nextBlue = Math.round(hueToRgb(p, q, hue - 1 / 3) * 255);

  return `#${nextRed.toString(16).padStart(2, "0")}${nextGreen
    .toString(16)
    .padStart(2, "0")}${nextBlue.toString(16).padStart(2, "0")}`;
}

function getDefaultSpaceSize(character) {
  return Math.max(character.defaultText.s, 18);
}

function base64ToBlob(
  base64Data,
  contentType = "image/png",
  sliceSize = 512
) {
  const byteCharacters = atob(base64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);

    for (let index = 0; index < slice.length; index += 1) {
      byteNumbers[index] = slice.charCodeAt(index);
    }

    byteArrays.push(new Uint8Array(byteNumbers));
  }

  return new Blob(byteArrays, { type: contentType });
}

export default function useStickerEditor() {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);

  const [fontsReady, setFontsReady] = useState(() => {
    if (typeof document === "undefined" || !document.fonts?.check) {
      return true;
    }

    return document.fonts.check("12px YurukaStd");
  });
  const [infoOpen, setInfoOpen] = useState(false);
  const [feedback, setFeedback] = useState({ open: false, message: "" });
  const [dominantColor, setDominantColor] = useState(DEFAULT_DOMINANT_COLOR);
  const [backgroundColor, setBackgroundColor] = useState(
    DEFAULT_BACKGROUND_COLOR
  );
  const [characterIndex, setCharacterIndex] = useState(DEFAULT_CHARACTER_INDEX);
  const [text, setText] = useState(
    characters[DEFAULT_CHARACTER_INDEX].defaultText.text
  );
  const [position, setPosition] = useState({
    x: characters[DEFAULT_CHARACTER_INDEX].defaultText.x,
    y: characters[DEFAULT_CHARACTER_INDEX].defaultText.y,
  });
  const [fontSize, setFontSize] = useState(
    characters[DEFAULT_CHARACTER_INDEX].defaultText.s
  );
  const [spaceSize, setSpaceSize] = useState(
    getDefaultSpaceSize(characters[DEFAULT_CHARACTER_INDEX])
  );
  const [rotate, setRotate] = useState(
    characters[DEFAULT_CHARACTER_INDEX].defaultText.r
  );
  const [curve, setCurve] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const currentCharacter = characters[characterIndex];
  const imageSrc = `/img/${currentCharacter.img}`;
  const isCopySupported =
    Boolean(ClipboardItemCtor) && Boolean(navigator.clipboard?.write);

  useEffect(() => {
    if (fontsReady || !document.fonts?.ready) {
      return undefined;
    }

    let active = true;

    document.fonts.ready.then(() => {
      if (active) {
        setFontsReady(true);
      }
    });

    return () => {
      active = false;
    };
  }, [fontsReady]);

  useEffect(() => {
    const nextDefaults = currentCharacter.defaultText;
    const image = new Image();
    const averageColor = new FastAverageColor();
    let active = true;
    let destroyed = false;

    const destroyAverageColor = () => {
      if (destroyed) return;

      destroyed = true;
      averageColor.destroy();
    };

    setText(nextDefaults.text);
    setPosition({
      x: nextDefaults.x,
      y: nextDefaults.y,
    });
    setRotate(nextDefaults.r);
    setFontSize(nextDefaults.s);
    setSpaceSize(getDefaultSpaceSize(currentCharacter));
    setCurve(false);
    setLoaded(false);

    image.src = imageSrc;
    image.onload = () => {
      if (!active) return;

      const color = averageColor.getColor(image, { algorithm: "sqrt" });

      imageRef.current = image;
      setDominantColor(color.hex);
      setBackgroundColor(desaturateColor(color.hex));
      setLoaded(true);
      destroyAverageColor();
    };
    image.onerror = () => {
      if (!active) return;

      imageRef.current = null;
      setLoaded(true);
      destroyAverageColor();
    };

    return () => {
      active = false;
      destroyAverageColor();
    };
  }, [currentCharacter, imageSrc]);

  const angle = useMemo(() => (Math.PI * text.length) / 7, [text.length]);

  const draw = useCallback(
    (context) => {
      const canvas = context.canvas;
      const previewImage = imageRef.current;

      canvas.width = 296;
      canvas.height = 256;

      if (loaded && previewImage && fontsReady) {
        const hRatio = canvas.width / previewImage.width;
        const vRatio = canvas.height / previewImage.height;
        const ratio = Math.min(hRatio, vRatio);
        const centerShiftX = (canvas.width - previewImage.width * ratio) / 2;
        const centerShiftY = (canvas.height - previewImage.height * ratio) / 2;

        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(
          previewImage,
          0,
          0,
          previewImage.width,
          previewImage.height,
          centerShiftX,
          centerShiftY,
          previewImage.width * ratio,
          previewImage.height * ratio
        );

        context.font = `${fontSize}px YurukaStd, SSFangTangTi`;
        context.lineWidth = 9;
        context.save();
        context.translate(position.x, position.y);
        context.rotate(rotate / 10);
        context.textAlign = "center";
        context.strokeStyle = "white";
        context.fillStyle = currentCharacter.color;

        const lines = text.split("\n");

        if (curve) {
          for (const line of lines) {
            if (!line.length) {
              continue;
            }

            for (let index = 0; index < line.length; index += 1) {
              context.rotate(angle / line.length / 2.5);
              context.save();
              context.translate(0, -fontSize * 3.5);
              context.strokeText(line[index], 0, 0);
              context.fillText(line[index], 0, 0);
              context.restore();
            }
          }
        } else {
          let offsetY = 0;

          for (const line of lines) {
            context.strokeText(line, 0, offsetY);
            context.fillText(line, 0, offsetY);
            offsetY += spaceSize;
          }
        }

        context.restore();
        return;
      }

      context.clearRect(0, 0, canvas.width, canvas.height);
      context.fillStyle = PLACEHOLDER_BACKGROUND;
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.font = "18px sans-serif";
      context.fillStyle = "#f7f2fa";
      context.textAlign = "center";
      context.fillText(
        "Pick a character to start",
        canvas.width / 2,
        canvas.height / 2
      );
      context.font = "13px sans-serif";
      context.fillStyle = "#b7adbf";
      context.fillText(
        "先选择一张贴纸，再开始编辑文字",
        canvas.width / 2,
        canvas.height / 2 + 26
      );
    },
    [
      angle,
      currentCharacter.color,
      curve,
      fontSize,
      fontsReady,
      loaded,
      position,
      rotate,
      spaceSize,
      text,
    ]
  );

  const openInfo = useCallback(() => {
    setInfoOpen(true);
  }, []);

  const closeInfo = useCallback(() => {
    setInfoOpen(false);
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedback((current) => ({ ...current, open: false }));
  }, []);

  const showFeedback = useCallback((message) => {
    setFeedback({ open: true, message });
  }, []);

  const handleCurveChange = useCallback((event) => {
    const checked = event.target.checked;

    setCurve(checked);
    if (checked) {
      setPosition({
        x: 100,
        y: 150,
      });
    }
  }, []);

  const handleHorizontalPositionChange = useCallback((_, value) => {
    setPosition((current) => ({ ...current, x: value }));
  }, []);

  const handleVerticalPositionChange = useCallback(
    (_, value) => {
      setPosition((current) => ({
        ...current,
        y: curve ? 256 + fontSize * 3 - value : 256 - value,
      }));
    },
    [curve, fontSize]
  );

  const copy = useCallback(async () => {
    const canvas = canvasRef.current;

    if (!canvas || !isCopySupported) {
      showFeedback("Copy is unavailable here / 当前环境暂不支持复制图片");
      return;
    }

    try {
      await navigator.clipboard.write([
        new ClipboardItemCtor({
          "image/png": base64ToBlob(canvas.toDataURL().split(",")[1]),
        }),
      ]);

      showFeedback("Copied image to clipboard / 已复制到剪贴板");
    } catch {
      showFeedback("Copy failed / 复制失败，请尝试下载图片");
    }
  }, [isCopySupported, showFeedback]);

  const download = useCallback(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      showFeedback("Preview is not ready yet / 预览还没准备好");
      return;
    }

    const link = document.createElement("a");

    link.download = `${currentCharacter.name}_sekai-stickers.png`;
    link.href = canvas.toDataURL();
    link.click();

    showFeedback("Downloading image... / 正在下载图片");
  }, [currentCharacter.name, showFeedback]);

  const verticalSliderValue = curve
    ? 256 - position.y + fontSize * 3
    : 256 - position.y;

  return {
    canvasRef,
    backgroundColor,
    characterIndex,
    closeFeedback,
    closeInfo,
    copy,
    currentCharacter,
    curve,
    dominantColor,
    download,
    draw,
    feedback,
    fontSize,
    handleCurveChange,
    handleHorizontalPositionChange,
    handleVerticalPositionChange,
    infoOpen,
    isCopySupported,
    openInfo,
    position,
    rotate,
    setCharacterIndex,
    setFontSize,
    setRotate,
    setSpaceSize,
    setText,
    spaceSize,
    text,
    verticalSliderValue,
  };
}
